'use client'

import * as React from 'react'
import { ProductCard } from '@web/components/product-card/product-card'
import type { Locales } from 'cf-i18n/helpers'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import type { Language } from 'codegen/types'
import { useInfiniteLoadingProducts } from '../../_hooks/use-infinite-loading-products'
import { tw } from 'utils/classnames'
import { SpinnerFasIcon } from '@web/components/icons/spinner-fas'
import { useTranslations } from 'next-intl'

interface Props {
  locale: Locales
  slug: string
  orderBy: string
  limit: number
  hidePriceSection?: boolean
}

export function MarketplaceInfiniteProductLoader({
  locale,
  slug,
  orderBy,
  limit,
  hidePriceSection = false,
}: Props) {
  const { ref, products, isFetchingNextPage } = useInfiniteLoadingProducts({
    slug,
    orderBy,
    limit,
    locale: locale as Language,
  })
  const t = useTranslations('components.products_pagination')
  return (
    <>
      {products.map(product => (
        <ProductCard
          key={product.id}
          productId={String(product.id || 0)}
          productUrl={addOrReplaceLocaleInUrl(`/product/${product.slug}`, locale as Language)}
          imageClassName="aspect-[3/2] object-cover"
          image={product.thumbnailUrl || ''}
          name={product.title || ''}
          alt={product.title || ''}
          category={product.category?.parents?.[0]?.name ?? product.category?.name ?? ''}
          hidePriceSection={hidePriceSection}
          isLegacy
        />
      ))}
      <div
        ref={ref}
        className={tw(
          'flex h-px w-full items-center justify-center bg-transparent py-8',
          'col-span-2 min-[992px]:col-span-3',
          'min-[1200px]:col-span-4',
        )}
      >
        {isFetchingNextPage ? (
          <div className="flex items-center justify-center gap-3">
            <SpinnerFasIcon className="inline size-8 animate-spin" />
            <span className="text-20">{t('loading_more')}</span>
          </div>
        ) : null}
      </div>
    </>
  )
}
